<template>
  <v-row justify="center">
    <v-dialog v-model="open" width="600px">
      <v-card>
        <v-toolbar elevation="0">
          <v-toolbar-title class="pl-3 text--subtitle">
            <v-icon color="primary" style="padding-bottom: 2px">
              {{ drmValue ? 'mdi-shield-off-outline' : 'mdi-shield-check' }}
            </v-icon>
            {{
              drmValue
                ? `${$t('message.actions.disable')} ${$t(
                    'message.commom.resource'
                  )}`
                : `${$t('message.actions.active')} DRM (Watermark) `
            }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn icon @click="handleClose()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="pl-7 pr-5 py-0">
          <v-row no-gutters>
            <v-col cols="12">
              <template v-if="!drmValue">
                <p v-html="$t('message.pages.drm.activeDrmText')" />
                <p
                  class="drm__warning-cost"
                  v-html="$t('message.pages.drm.disclaimerDRMCost')"
                />
                <p>
                  <span>{{ $t('message.pages.drm.articleAboutIntro') }}</span>
                  <a :href="drmLink" target="_blank">
                    {{ $t('message.pages.drm.articleAbout') }}</a
                  >
                </p>
              </template>
              <span
                v-else
                v-html="$t('message.pages.drm.disableDrmText')"
              ></span>
            </v-col>
            <v-row no-gutters v-if="drmValue">
              <v-col cols="12" class="my-3">
                <b>{{
                  $t('message.pages.video.deleteModal.confirmRemoveMessage2')
                }}</b>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="password"
                  class="pr-2"
                  :label="$t('message.password')"
                  dense
                  outlined
                  autofocus
                  autocomplete="off"
                  :error="errors.invalidAccountError"
                  :messages="loginPasswordErrorMessage"
                  :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPass ? 'text' : 'password'"
                  @click:append="showPass = !showPass"
                >
                  <template v-slot:message="{ message }">
                    <span
                      class="red--text caption align-text"
                      v-if="errors.invalidAccountError"
                    >
                      <v-icon color="red" size="18" class="pb-1">
                        mdi-alert-circle
                      </v-icon>
                      {{ message }}
                    </span>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-8 pb-6 pt-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="px-4"
            min-width="160px"
            :disabled="isFetching"
            @click="handleClose"
          >
            {{
              drmValue
                ? $t('message.close')
                : $t('message.pages.drm.notYetAction')
            }}
          </v-btn>
          <v-btn
            min-width="160px"
            color="primary"
            :disabled="isFetching || checkPassword"
            :loading="isFetching"
            @click="handleConfirm"
          >
            <span class="mx-2">
              {{
                drmValue
                  ? $t('message.actions.disable')
                  : $t('message.pages.drm.activeNow')
              }}
            </span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
// external imports
import { mapActions } from 'vuex'

// internal imports
import { DRM_LINK_PT_BR, DRM_LINK_EN_US } from '@/config/envs'

// API
import drmApi from '@/services/drmApi'

// Components

export default {
  name: 'DrmWarning',
  components: {},
  data: () => ({
    open: false,
    isFetching: false,
    drmValue: false,
    showPass: false,
    password: '',
    errors: {
      invalidAccountError: false,
    },
  }),
  computed: {
    checkPassword() {
      if (this.drmValue) {
        return !this.password
      } else {
        return false
      }
    },
    userInfo() {
      return this.$store.getters['session/userAttributes']
    },
    loginPasswordErrorMessage() {
      const i18n = this.$i18next

      if (this.errors.invalidAccountError) {
        return i18n.t(
          'message.pages.video.deleteModal.errors.invalidAccountError'
        )
      }
      if (this.errors.internalErrorException) {
        return i18n.t('message.pages.video.deleteModal.errors.internalError')
      }
      if (this.errors.unexpectedError) {
        return i18n.t('message.errors.unexpectedError') + this.unknownError
      }
      return ''
    },
    isLocalePT_BR() {
      return this.$i18next.resolvedLanguage === 'pt'
    },
    drmLink() {
      return this.isLocalePT_BR ? DRM_LINK_PT_BR : DRM_LINK_EN_US
    },
  },
  watch: {},
  mounted() {
    this.$root.$on('openDrmWarning', (drmValue) => {
      this.drmValue = drmValue
      this.open = true
    })
  },
  beforeDestroy() {
    this.$root.$off('openDrmWarning')
  },
  methods: {
    ...mapActions('session', ['signInUser']),
    async confirmUserPassword() {
      this.password = this.password.trim()
      try {
        await this.signInUser({
          username: this.userInfo.email,
          password: this.password,
        })
        return true
      } catch (error) {
        if (!error.code) {
          const anyError = JSON.parse(JSON.stringify(error))
          if (anyError?.message?.includes('Network Error')) {
            this.changePasswordLoading = false
            this.unknownError = 'Code: 500'
            this.newPasswordError.newPasswordError = true
            return
          }
        }
        if (
          error.code === 'NotAuthorizedException' ||
          error.code === 'InvalidParameterException'
        ) {
          this.errors.invalidAccountError = true
        }
        if (error.code === 'InternalErrorException') {
          this.errors.internalErrorException = true
        }
        this.isFetching = false
        return false
      }
    },
    handleConfirm() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
      if (this.drmValue) {
        this.handleDeactivateDRM()
      } else {
        this.handleActiveDRM()
      }
    },
    async handleActiveDRM() {
      this.isFetching = true
      try {
        await drmApi.activateDrm()
        this.$root.$emit('switchDrmValue')
      } catch (e) {
        this.isFetching = false
        console.log('e', e)
      }
      this.handleClose()
      this.isFetching = false
    },
    async handleDeactivateDRM() {
      this.isFetching = true
      let confirmResponse = await this.confirmUserPassword()
      if (!confirmResponse) return
      try {
        drmApi.deactivateDrm()
        this.$root.$emit('switchDrmValue')
      } catch (e) {
        this.isFetching = false
        console.log('e', e)
      }
      this.handleClose()
      this.isFetching = false
    },
    handleClose() {
      this.open = false
    },
  },
}
</script>

<style>
.cardTitle {
  font-weight: 600;
  font-size: 24px;
  line-height: 36px;
}
</style>

<style lang="scss" scoped>
.drm {
  &__warning-cost {
    &::v-deep {
      b {
        color: #f44336;
      }
    }
  }
}
</style>
